"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import { cn } from "@/lib/utils"
import { Image as ImageIcon, Star, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react"

export interface GalleryImage {
  label: string         // e.g. "赛博朋克风"
  dimensions?: string   // e.g. "1080×1920"
  recommended?: boolean
  /** Gradient placeholder colors */
  colors?: [string, string]
  /** Real image URL — if provided, shown instead of gradient */
  src?: string
}

export function ImageGalleryCard({
  title,
  images,
  className,
}: {
  title?: string
  images: GalleryImage[]
  className?: string
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
  }, [])

  useEffect(() => {
    checkScroll()
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(checkScroll)
    ro.observe(el)
    return () => ro.disconnect()
  }, [checkScroll, images])

  const scroll = useCallback((dir: "left" | "right") => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: dir === "left" ? -160 : 160, behavior: "smooth" })
  }, [])

  const defaultGradients: [string, string][] = [
    ["#6366f1", "#8b5cf6"],
    ["#06b6d4", "#3b82f6"],
    ["#f43f5e", "#ec4899"],
    ["#f59e0b", "#ef4444"],
    ["#10b981", "#06b6d4"],
  ]

  return (
    <div className={cn(
      "rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm overflow-hidden",
      className,
    )}>
      {title && (
        <div className="px-4 py-2.5 border-b border-border/40 bg-secondary/20">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold text-foreground">{title}</span>
            <span className="text-micro px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium ml-auto">
              {images.length} 张
            </span>
          </div>
        </div>
      )}

      <div className="relative group/gallery">
        {/* Left arrow */}
        <button
          type="button"
          aria-label="向左滚动"
          onClick={() => scroll("left")}
          className={cn(
            "absolute left-0 top-0 bottom-0 z-10 w-8 flex items-center justify-center",
            "bg-linear-to-r from-card/90 to-transparent",
            "transition-opacity duration-200",
            canScrollLeft ? "opacity-100" : "opacity-0 pointer-events-none",
          )}
        >
          <ChevronLeft className="h-4 w-4 text-foreground/70" />
        </button>

        {/* Scroll track */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="p-3 flex gap-2.5 overflow-x-auto no-scrollbar"
        >
          {images.map((img, i) => {
            const [c1, c2] = img.colors || defaultGradients[i % defaultGradients.length]
            return (
              <div
                key={i}
                className="relative shrink-0 group cursor-pointer"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div
                  className={cn(
                    "w-28 h-36 rounded-lg transition-all duration-200 overflow-hidden",
                    hoveredIndex === i && "ring-2 ring-primary ring-offset-2 ring-offset-background scale-105 shadow-lg",
                  )}
                  style={!img.src ? {
                    background: `linear-gradient(135deg, ${c1}, ${c2})`,
                  } : undefined}
                >
                  {img.src ? (
                    <img
                      src={img.src}
                      alt={img.label}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-black/10">
                      <div className="w-12 h-1.5 rounded-full bg-white/40" />
                      <div className="w-8 h-1 rounded-full bg-white/20" />
                      <ImageIcon className="h-6 w-6 text-white/30 mt-2" />
                    </div>
                  )}

                  <div className={cn(
                    "absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-150",
                    hoveredIndex === i ? "opacity-100" : "opacity-0",
                  )}>
                    <ZoomIn className="h-5 w-5 text-white" />
                  </div>
                </div>

                {img.recommended && (
                  <div className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-white shadow-sm">
                    <Star className="h-3 w-3 fill-current" />
                  </div>
                )}

                <div className="mt-1.5 text-center">
                  <p className="text-caption font-medium text-foreground truncate w-28">{img.label}</p>
                  {img.dimensions && (
                    <p className="text-micro text-muted-foreground">{img.dimensions}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Right arrow */}
        <button
          type="button"
          aria-label="向右滚动"
          onClick={() => scroll("right")}
          className={cn(
            "absolute right-0 top-0 bottom-0 z-10 w-8 flex items-center justify-center",
            "bg-linear-to-l from-card/90 to-transparent",
            "transition-opacity duration-200",
            canScrollRight ? "opacity-100" : "opacity-0 pointer-events-none",
          )}
        >
          <ChevronRight className="h-4 w-4 text-foreground/70" />
        </button>
      </div>
    </div>
  )
}
